import { makeAbsolutePhotoUrl } from '../../services/api';
import type { CommonArea } from '../../services/commonAreaReservationService';

/**
 * Which photos an área comum has, and which one represents it.
 *
 * The backend has carried ten photo slots since migration 170, and the apps have shown them in a
 * carousel for as long — the web typed them away entirely, so its screens drew a grey placeholder
 * next to a perfectly good photo.
 *
 * Shared between the list card (which shows only the main one) and the booking modal (which shows
 * them all), so the two can never disagree about which picture is "the" picture.
 *
 * See areaPhotos.test.ts.
 */

const SLOT_KEYS = [
  'photo_url',
  'photo_url_2',
  'photo_url_3',
  'photo_url_4',
  'photo_url_5',
  'photo_url_6',
  'photo_url_7',
  'photo_url_8',
  'photo_url_9',
  'photo_url_10',
] as const;

/**
 * Every photo the área has, in slot order, already absolute.
 *
 * Gaps are closed rather than preserved: deleting photo 2 leaves an empty slot, and a carousel
 * that honoured it would render a broken frame in the middle. The URLs are made absolute here
 * because the API stores them as `/api/v1/images/...`, which 404s against the web origin.
 */
export function areaPhotos(area: CommonArea | null | undefined): string[] {
  if (!area) return [];
  const record = area as unknown as Record<string, string | undefined>;
  return SLOT_KEYS
    .map(key => record[key])
    .filter((url): url is string => !!url && url.trim() !== '')
    .map(url => makeAbsolutePhotoUrl(url.trim()))
    .filter((url): url is string => !!url);
}

/**
 * The photo that stands for the área — the first slot that actually holds one.
 *
 * Not strictly `photo_url`: an área whose first photo was deleted still has a face to show, and
 * falling back is what the apps do.
 */
export function areaMainPhoto(area: CommonArea | null | undefined): string | null {
  return areaPhotos(area)[0] ?? null;
}
